import { useCallback, useRef, useState } from "react"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import {
  FileCheckIcon,
  PencilIcon,
  UploadIcon,
  FileTextIcon,
  Trash2Icon,
  PlusIcon,
  BookmarkIcon,
} from "lucide-react"
import { cn } from "@/lib/utils"
import { formatBytes } from "./helpers"
import type { CoversheetTemplate } from "./types"

type CoversheetConfigProps = {
  includeCoversheet: boolean
  setIncludeCoversheet: (value: boolean) => void
  coversheetMode: "build" | "upload"
  setCoversheetMode: (mode: "build" | "upload") => void
  coversheetFile: File | null
  setCoversheetFile: (file: File | null) => void
  templates: CoversheetTemplate[]
  activeTemplateId: string | null
  setActiveTemplateId: (id: string | null) => void
  onCreateTemplate?: () => void
  templateSkipsBuilder: boolean
  activeTemplate: CoversheetTemplate | null
  inlineSubject: string
  setInlineSubject: (value: string) => void
  inlineMessage: string
  setInlineMessage: (value: string) => void
}

export function CoversheetConfig({
  includeCoversheet,
  setIncludeCoversheet,
  coversheetMode,
  setCoversheetMode,
  coversheetFile,
  setCoversheetFile,
  templates,
  activeTemplateId,
  setActiveTemplateId,
  onCreateTemplate,
  templateSkipsBuilder,
  activeTemplate,
  inlineSubject,
  setInlineSubject,
  inlineMessage,
  setInlineMessage,
}: CoversheetConfigProps) {
  const [isDragging, setIsDragging] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }, [])

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }, [])

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    const file = e.dataTransfer.files[0]
    if (file && file.type === "application/pdf") setCoversheetFile(file)
  }, [setCoversheetFile])

  return (
    <div className="rounded-lg border overflow-hidden">
      {/* Toggle row */}
      <div className="flex items-center justify-between p-3">
        <div className="flex items-center gap-3">
          <FileCheckIcon className="size-4 text-muted-foreground" />
          <div>
            <p className="text-sm font-medium">Include Cover Sheet</p>
            <p className="text-xs text-muted-foreground">
              Build or upload a professional cover page
            </p>
          </div>
        </div>
        <Switch
          checked={includeCoversheet}
          onCheckedChange={setIncludeCoversheet}
          aria-label="Include cover sheet"
        />
      </div>

      {/* Options (when enabled) */}
      {includeCoversheet && (
        <div className="border-t p-3 space-y-3 bg-muted/20">
          {/* Build or Upload toggle */}
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant={coversheetMode === "build" ? "default" : "outline"}
              className="h-8 text-xs"
              onClick={() => setCoversheetMode("build")}
            >
              <PencilIcon className="size-3 mr-1.5" />
              Build
            </Button>
            <Button
              type="button"
              size="sm"
              variant={coversheetMode === "upload" ? "default" : "outline"}
              className="h-8 text-xs"
              onClick={() => setCoversheetMode("upload")}
            >
              <UploadIcon className="size-3 mr-1.5" />
              Upload PDF
            </Button>
          </div>

          {/* BUILD mode -- template selector */}
          {coversheetMode === "build" && (
            <div className="space-y-3">
              <div className="space-y-2">
                <Label className="text-xs flex items-center gap-1.5">
                  <BookmarkIcon className="size-3" />
                  Template
                </Label>
                <div className="flex items-center gap-2">
                  <Select
                    value={activeTemplateId ?? "__none__"}
                    onValueChange={(v) => {
                      if (v === "__create__") {
                        onCreateTemplate?.()
                        return
                      }
                      setActiveTemplateId(v === "__none__" ? null : v)
                    }}
                  >
                    <SelectTrigger className="h-8 text-xs flex-1">
                      <SelectValue placeholder="Start from scratch" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">
                        Start from scratch
                      </SelectItem>
                      {templates.map((t) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.name}
                        </SelectItem>
                      ))}
                      <SelectItem value="__create__">
                        <span className="flex items-center gap-1.5">
                          <PlusIcon className="size-3" />
                          Create New Template
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* If template is complete, show inline per-fax fields */}
              {templateSkipsBuilder && activeTemplate && (
                <div className="space-y-3 rounded-lg border p-3 bg-background">
                  <p className="text-xs text-muted-foreground">
                    Using template{" "}
                    <span className="font-medium text-foreground">
                      {activeTemplate.name}
                    </span>
                    . Fill in the per-fax details below.
                  </p>
                  <div className="space-y-2">
                    <Label className="text-xs">Subject</Label>
                    <Input
                      placeholder="Subject line for this fax"
                      className="h-8 text-sm"
                      value={inlineSubject}
                      onChange={(e) => setInlineSubject(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Notes / Comments</Label>
                    <Textarea
                      placeholder="Optional notes for the recipient..."
                      rows={3}
                      className="text-sm"
                      value={inlineMessage}
                      onChange={(e) => setInlineMessage(e.target.value)}
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* UPLOAD mode -- drop zone */}
          {coversheetMode === "upload" && (
            <div>
              {!coversheetFile ? (
                <div
                  className={cn(
                    "rounded-lg border-2 border-dashed transition-colors cursor-pointer",
                    isDragging
                      ? "border-primary bg-primary/5"
                      : "border-muted-foreground/25 hover:border-muted-foreground/40"
                  )}
                  onDragOver={onDragOver}
                  onDragLeave={onDragLeave}
                  onDrop={onDrop}
                  onClick={() => inputRef.current?.click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault()
                      inputRef.current?.click()
                    }
                  }}
                  aria-label="Upload coversheet PDF"
                >
                  <input
                    ref={inputRef}
                    type="file"
                    accept=".pdf"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (file) setCoversheetFile(file)
                      e.target.value = ""
                    }}
                  />
                  <div className="flex flex-col items-center gap-2 py-6 px-4 text-center">
                    <FileTextIcon className="size-6 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">
                        {isDragging
                          ? "Drop your cover sheet here..."
                          : "Upload a cover sheet PDF"}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Click to browse or drag and drop
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 rounded-md border bg-muted/40 px-3 py-2">
                  <FileTextIcon className="size-4 text-red-500 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {coversheetFile.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatBytes(coversheetFile.size)}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-7 text-muted-foreground hover:text-destructive"
                    onClick={() => setCoversheetFile(null)}
                  >
                    <Trash2Icon className="size-3.5" />
                    <span className="sr-only">Remove coversheet</span>
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
